import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import PaymentModal from "../components/PaymentModal";
import DashboardLayout from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { getProfile } from "../services/api";

export default function UserPricing() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'annual'
  const [paymentModal, setPaymentModal] = useState({ isOpen: false, plan: null, planName: '', price: 0, billingPeriod: 'monthly' });
  const { logout } = useAuth();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await getProfile();
        if (res.ok) {
          setProfile(res.data);
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleLogout = () => {
    logout();
    localStorage.removeItem("push_registered_v1");
  };

  const openPaymentModal = (plan, planName, price) => {
    console.log('🎫 Opening payment modal:', {
      plan,
      planName,
      price,
      billingPeriod
    });
    setPaymentModal({ isOpen: true, plan, planName, price, billingPeriod });
  };

  const closePaymentModal = () => {
    setPaymentModal({ isOpen: false, plan: null, planName: '', price: 0, billingPeriod: 'monthly' });
  };

  const handlePaymentSuccess = (newPlan) => {
    setProfile(prev => ({ ...prev, plan: newPlan }));
    closePaymentModal();
    // You might want to show a success message here
  };

  if (loading) {
    return (
      <DashboardLayout onLogout={handleLogout}>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500/20 border-t-emerald-500"></div>
          <span className="ml-2 text-gray-400">Loading pricing...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout onLogout={handleLogout}>
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 px-4 sm:px-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Upgrade Plan</h1>
          <p className="text-sm sm:text-base text-gray-400">Choose the perfect plan for your monitoring needs</p>
          
          {/* Billing Period Toggle */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-white' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                billingPeriod === 'annual' ? 'bg-emerald-500' : 'bg-white/10'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingPeriod === 'annual' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingPeriod === 'annual' ? 'text-white' : 'text-gray-500'}`}>
              Annual
            </span>
            {billingPeriod === 'annual' && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400">
                Save 17% 🎉
              </span>
            )}
          </div>
        </div>

        {/* Current Plan Status */}
        <Card className="bg-gradient-to-r from-emerald-500/5 to-cyan-500/5 border border-emerald-500/20 shadow-lg">
          <Card.Header>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <Card.Title className="text-lg sm:text-xl font-bold text-white">Your Current Plan</Card.Title>
                <Card.Description className="text-sm sm:text-base text-gray-300">
                  You're currently on the <strong className="text-white">{profile?.plan?.charAt(0).toUpperCase() + profile?.plan?.slice(1) || 'Free'}</strong> plan
                  {profile?.subscription_expires_at && profile?.plan !== 'free' && (
                    <span className="block mt-1 text-xs sm:text-sm">
                      Member until <strong>{new Date(profile.subscription_expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                    </span>
                  )}
                </Card.Description>
              </div>
              <div className="text-left sm:text-right">
                <Badge variant={profile?.plan === 'free' ? 'info' : 'success'} className="text-base sm:text-lg px-4 sm:px-6 py-2 sm:py-3 font-semibold">
                  {profile?.plan?.charAt(0).toUpperCase() + profile?.plan?.slice(1) || 'Free'} Plan
                </Badge>
              </div>
            </div>
          </Card.Header>
          <Card.Content>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 gap-4">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm sm:text-base text-white font-medium">Plan Active</p>
                  <p className="text-xs sm:text-sm text-gray-400">
                    {profile?.plan === 'free' ? 'Enjoy our free monitoring service' : 'Thank you for being a premium subscriber'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button as={Link} to="/dashboard" variant="outline" className="text-sm w-full sm:w-auto">
                  Go to Dashboard
                </Button>
                <Button as={Link} to="/profile" variant="primary" className="text-sm w-full sm:w-auto">
                  Manage Account
                </Button>
              </div>
            </div>
          </Card.Content>
        </Card>

        {/* Pricing Plans */}
        <div className="grid gap-6 sm:gap-8 lg:grid-cols-3">
          {/* Free Plan */}
          <Card className={`flex flex-col min-h-[550px] ${profile?.plan === 'free' ? 'ring-2 ring-emerald-500/50 border-emerald-500/30 bg-emerald-500/5' : ''}`}>
            <Card.Header>
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Free</h3>
                <p className="text-sm sm:text-base text-gray-400 mb-4">Perfect for getting started</p>
                <div className="mb-4 sm:mb-6">
                  <span className="text-4xl sm:text-5xl font-bold text-white">₱0</span>
                  <span className="text-sm sm:text-base text-gray-500">/month</span>
                </div>
                {profile?.plan === 'free' ? (
                  <div className="space-y-2">
                    <Badge variant="success" className="w-full py-2 sm:py-3 text-base sm:text-lg font-bold bg-green-500 text-white border-green-600">
                      ✓ Current Plan
                    </Badge>
                    <p className="text-emerald-400 text-sm font-medium">You're all set!</p>
                  </div>
                ) : (
                  <Button 
                    onClick={() => openPaymentModal('free', 'Free', 0)}
                    className="w-full py-2.5 sm:py-3 font-semibold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700 px-4 text-sm"
                    variant="outline"
                  >
                    Downgrade to Free
                  </Button>
                )}
              </div>
            </Card.Header>
            
            <Card.Content className="flex-grow">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base text-gray-300">3 websites monitoring</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base text-gray-300">Email alerts</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base text-gray-300">5-minute check intervals</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base text-gray-300">30-day uptime history</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base text-gray-300">SSL monitoring</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base text-gray-300">Community support</span>
                </div>
              </div>
            </Card.Content>
          </Card>

          {/* Pro Plan */}
          <Card className={`flex flex-col min-h-[550px] ${profile?.plan === 'pro' ? 'ring-2 ring-emerald-500/50 border-emerald-500/30 bg-emerald-500/5' : 'border border-emerald-500/30'} relative`}>
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              <span className="bg-emerald-500 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">Most Popular</span>
            </div>
            <Card.Header>
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Pro</h3>
                <p className="text-sm sm:text-base text-gray-400 mb-4">For growing businesses</p>
                <div className="mb-4 sm:mb-6">
                  {billingPeriod === 'annual' ? (
                    <>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl text-gray-600 line-through">₱9,000</span>
                        <div className="flex flex-col items-center">
                          <span className="text-4xl sm:text-5xl font-bold text-white">₱4,500</span>
                          <span className="text-lg text-gray-500">$90 USD</span>
                        </div>
                      </div>
                      <span className="text-sm sm:text-base text-gray-500">/year (save ₱4,500)</span>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col items-center">
                        <span className="text-4xl sm:text-5xl font-bold text-white">₱750</span>
                        <span className="text-lg text-gray-500">$15 USD</span>
                      </div>
                      <span className="text-sm sm:text-base text-gray-500">/month</span>
                    </>
                  )}
                </div>
                {profile?.plan === 'pro' ? (
                  <div className="space-y-2">
                    <Badge variant="success" className="w-full py-2 sm:py-3 text-base sm:text-lg font-bold bg-green-500 text-white border-green-600">
                      ✓ Current Plan
                    </Badge>
                    <p className="text-emerald-400 text-sm font-medium">You're all set!</p>
                  </div>
                ) : (
                  <Button 
                    onClick={() => openPaymentModal('pro', 'Pro', billingPeriod === 'annual' ? 4500 : 750)}
                    className="w-full py-2.5 sm:py-3 font-semibold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm bg-blue-600 text-white hover:bg-blue-700 focus:ring-emerald-500 dark:bg-blue-600 dark:hover:bg-blue-700 px-4 text-sm"
                    variant="primary"
                  >
                    {profile?.plan === 'free' ? "Upgrade to Pro" : "Change to Pro"}
                  </Button>
                )}
              </div>
            </Card.Header>
            
            <Card.Content className="flex-grow">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">15 websites monitoring</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">Email + Push notifications</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">2-minute check intervals</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">90-day uptime history</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">Priority support</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">Custom alert emails</span>
                </div>
              </div>
            </Card.Content>
          </Card>

          {/* Enterprise Plan */}
          <Card className={`${profile?.plan === 'enterprise' ? 'ring-2 ring-emerald-500/50 border-emerald-500/30 bg-emerald-500/5' : ''}`}>
            <Card.Header>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
                <p className="text-gray-400 mb-4">For large organizations</p>
                <div className="mb-6">
                  {billingPeriod === 'annual' ? (
                    <>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl text-gray-600 line-through">₱17,400</span>
                        <div className="flex flex-col items-center">
                          <span className="text-5xl font-bold text-white">₱14,500</span>
                          <span className="text-lg text-gray-500">$290 USD</span>
                        </div>
                      </div>
                      <span className="text-gray-500">/year (save ₱2,900)</span>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col items-center">
                        <span className="text-5xl font-bold text-white">₱1,450</span>
                        <span className="text-lg text-gray-500">$29 USD</span>
                      </div>
                      <span className="text-gray-500">/month</span>
                    </>
                  )}
                </div>
                {profile?.plan === 'enterprise' ? (
                  <div className="space-y-2">
                    <Badge variant="success" className="w-full py-3 text-lg font-bold bg-green-500 text-white border-green-600">
                      ✓ Current Plan
                    </Badge>
                    <p className="text-emerald-400 text-sm font-medium">You're all set!</p>
                  </div>
                ) : (
                  <Button 
                    onClick={() => openPaymentModal('enterprise', 'Enterprise', billingPeriod === 'annual' ? 14500 : 1450)}
                    className="w-full py-3 font-semibold rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700 px-4 py-2 text-sm"
                    variant="outline"
                  >
                    {profile?.plan === 'free' || profile?.plan === 'pro' ? "Upgrade to Enterprise" : "Change to Enterprise"}
                  </Button>
                )}
              </div>
            </Card.Header>
            
            <Card.Content>
              <div className="space-y-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">200 websites monitoring</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">All notification types</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">30-second check intervals</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">1-year uptime history</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">24/7 phone support</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">API access & webhooks</span>
                </div>
              </div>
            </Card.Content>
          </Card>
        </div>

        {/* FAQ Section */}
        <Card>
          <Card.Header>
            <Card.Title>Frequently Asked Questions</Card.Title>
            <Card.Description>Common questions about our pricing plans</Card.Description>
          </Card.Header>
          <Card.Content>
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Can I change plans anytime?
                </h3>
                <p className="text-gray-400">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  What happens if I exceed my plan limits?
                </h3>
                <p className="text-gray-400">
                  We'll notify you when you're approaching your limits. You can upgrade or add extra sites as needed.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Do you offer refunds?
                </h3>
                <p className="text-gray-400">
                  Yes, we offer a 30-day money-back guarantee on all paid plans. No questions asked.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Is there a free trial?
                </h3>
                <p className="text-gray-400">
                  Our free plan is always free! For paid plans, we offer a 14-day free trial.
                </p>
              </div>
            </div>
          </Card.Content>
        </Card>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={paymentModal.isOpen}
          onClose={closePaymentModal}
          plan={paymentModal.plan}
          planName={paymentModal.planName}
          price={paymentModal.price}
          currentPlan={profile?.plan}
          onSuccess={handlePaymentSuccess}
          billingPeriod={paymentModal.billingPeriod}
        />
      </div>
    </DashboardLayout>
  );
}
